import React from 'react';
import { Navigate, useParams } from 'react-router';
import { Box, CircularProgress } from '@mui/material';

import { useCurrentUser } from '../auth/useCurrentUser';
import { firstGrantedSurfacePath, PermissionCode } from '../auth/permissionCatalog';
import { paths } from './paths';
import { RequiredRole } from './types';

/**
 * Client-side RBAC guard for a route's `role`/`permission` metadata. Wraps a
 * route element so a user who lacks the required role/permission can't reach
 * it by direct URL — the sidebar already hides the entry, this closes the
 * direct-navigation gap.
 *
 * Enforcement is defense-in-depth only — the API is independently gated
 * (loadAppUserRoles + requireRole / requirePermission), so this is UX, not
 * the security boundary.
 *
 * Flag off: behaves EXACTLY like the retired `RequireRole` (decisions §15 —
 * `ENFORCED_ROLES` is the same set, same fallback). Flag on: gates on
 * `permission` (or `permissionAnyOf` — granted when the user holds at least
 * one of the listed codes; today only the `insights` route uses it, R12) —
 * no `permission`/`permissionAnyOf` metadata means the route is explicitly
 * ungated (e.g. `guide`); `'super-admin'` checks `isSuperAdmin` directly. A
 * denied user is redirected to the first surface their permissions actually
 * grant (see `firstGrantedSurfacePath`) rather than a hard-coded dashboard,
 * since a v2 role may not include `dashboard`.
 *
 * Bare-chrome override (decisions §11, design §6.2): a `hide_chrome` holder
 * hitting an otherwise-ungated route (no `permission`/`permissionAnyOf` at
 * all — e.g. `guide`, `publish`) is bounced to their first granted surface
 * too, via `firstGrantedSurfacePath` — Insights when they hold a paired
 * insights permission, otherwise their real next surface, or
 * `/access-denied` for a zero-surface role (never an unconditional Insights
 * redirect, which would loop a misconfigured hide_chrome-without-insights
 * role into a page that denies them — adversarial review P1). So an ungated
 * route can't leak chrome-less pages to them. Gated routes don't need a
 * separate override — a denial already goes through the same
 * `firstGrantedSurfacePath` fallback.
 *
 * Loading-flash fix: `rbacV2Enabled` defaults to `false` on `useCurrentUser`
 * while `/api/me` is still resolving (no data yet), which is indistinguishable
 * from a genuine flag-off server. Without a guard, a `permission`/
 * `permissionAnyOf`-carrying route would race into the flag-off branch below
 * — which renders unconditionally for any non-`ENFORCED_ROLES` `role` (most
 * v2 routes carry no `role` at all) — mounting a page (and firing its own
 * data queries) that the real, once-resolved flag/permission state may deny
 * a moment later. So any route carrying `permission`/`permissionAnyOf`
 * renders the loading spinner instead, regardless of `rbacV2Enabled`'s
 * current value, until `/api/me` resolves. Flag-off users see at most a
 * sub-second spinner on those routes during that window — ratified as an
 * acceptable flag-off delta.
 */
const ENFORCED_ROLES = new Set<RequiredRole>(['flex', 'promoter', 'taskv2']);

// firstGrantedSurfacePath's zero-surface terminus — navigations that land
// here carry `state.reason: 'no_surfaces'` so AccessDenied can explain a
// zero-permission role instead of showing generic app-access copy (F7).
const ACCESS_DENIED_PATH = paths.accessDenied();

const Spinner: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

const RequirePermission: React.FC<{
  role?: RequiredRole;
  permission?: PermissionCode | 'super-admin';
  permissionAnyOf?: PermissionCode[];
  children: React.ReactNode;
}> = ({ role, permission, permissionAnyOf, children }) => {
  const currentUser = useCurrentUser();
  const { isSuperAdmin, hasRole, hasPermission, rbacV2Enabled, isLoading } = currentUser;
  const { tenantCode } = useParams<{ tenantCode: string }>();

  if ((permission || permissionAnyOf) && isLoading) {
    return <Spinner />;
  }

  if (!rbacV2Enabled) {
    // Legacy/unenforced role → render as if ungated (restores pre-#249 behavior).
    if (!role || !ENFORCED_ROLES.has(role)) {
      return <>{children}</>;
    }
    if (isLoading) {
      return <Spinner />;
    }
    if (isSuperAdmin || hasRole(role)) {
      return <>{children}</>;
    }
    const fallback = tenantCode ? `${paths.tenant(tenantCode)}/dashboard` : '/';
    return <Navigate to={fallback} replace />;
  }

  if (!permission && !permissionAnyOf) {
    if (!isLoading && tenantCode && hasPermission('hide_chrome')) {
      const target = firstGrantedSurfacePath(currentUser, tenantCode);
      return (
        <Navigate
          to={target}
          state={target === ACCESS_DENIED_PATH ? { reason: 'no_surfaces' } : undefined}
          replace
        />
      );
    }
    return <>{children}</>;
  }
  if (isLoading) {
    return <Spinner />;
  }
  const granted =
    permission === 'super-admin'
      ? isSuperAdmin
      : permission
        ? hasPermission(permission)
        : (permissionAnyOf ?? []).some(hasPermission);
  if (granted) {
    return <>{children}</>;
  }
  const fallback = tenantCode ? firstGrantedSurfacePath(currentUser, tenantCode) : '/';
  return (
    <Navigate
      to={fallback}
      state={fallback === ACCESS_DENIED_PATH ? { reason: 'no_surfaces' } : undefined}
      replace
    />
  );
};

export default RequirePermission;
